import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Card, CardBody, Container, Row, Col, Table, Button, CardHeader, CardFooter, Badge } from 'reactstrap';
import { RecieptData } from '../../../../../data/bills-payment/reciept/RecieptData';
import Breadcrumbs from '../../../../../common-elements/breadcrumbs/Breadcrumbs';
import { Image } from '../../../../../AbstractElements';
import { dynamicImage } from '../../../../../service';
import { useReactToPrint } from 'react-to-print';


function ViewRecieptSection() {
  const { t } = useTranslation();
  const { id } = useParams();

  const data = RecieptData.find(
    (user) => user.id.toString() === id
  );
  
  const contentRef = useRef<HTMLDivElement | null>(null);
  
  // CORRECTED: The 'content' property must be a function that returns the ref
  const handlePrint = useReactToPrint({ contentRef });

  if (!data) {
    return (
      <>
        <Breadcrumbs mainTitle={t("View Invoice")} parent={t("Bills Payment")} />
        <Container>
          <p>Invoice not found.</p>
        </Container>
      </>
    );
  }

  const subtotal = data.payment_amount;
  const tax = data.payment_transaction_charge;
  const paidAmount = subtotal + tax;
  const dueBalance = data.payment_status === "Completed" ? 0.00 : paidAmount;

  return (
    <>
      <Breadcrumbs
        mainTitle={t("View Invoice")}
        parent={t("Bills Payment")}
        parentLink={`${process.env.PUBLIC_URL}/billingpayments/invoice`}
      />
        
      <Container className="invoice-5 my-4">
        <Card>
          {/* The ref should wrap only the content you want to print */}
          <div ref={contentRef}>
            <CardHeader className="bg-primary d-flex justify-content-between align-items-center d-print-none">
              <h4 className="mb-0 text-white">Invoice</h4>
            </CardHeader>
            <CardBody className="p-4">
              <Row className="mb-4">
                {/* CHANGED: Used xs={6} for fixed width on all screen sizes */}
                <Col xs={6}>
                  <div className="comingsoon-inner text-center">
                    <Image src={dynamicImage("dashboard-8/Linnk_Group_logo.png")} alt="company-logo" style={{ width: "200px", height: "auto" }} />
                  </div>
                </Col>
                {/* CHANGED: Used xs={6} for fixed width on all screen sizes */}
                <Col xs={6}>
                  <h4>Invoice</h4>
                  <div className="d-flex justify-content-between p-2 border rounded mb-2">
                    <span>Invoice Number:</span>
                    <strong>{data.payment_invoice_no}</strong>
                  </div>
                   <div className="d-flex justify-content-between p-2 border rounded mb-2">
                    <span>Payment ID:</span>
                    <strong>{data.payment_identification_number}</strong>
                  </div>
                  <div className="d-flex justify-content-between p-2 border rounded mb-2">
                    <span>Payment Date:</span>
                    <strong>{new Date(data.payment_datetime).toLocaleString()}</strong>
                  </div>
                   <div className="d-flex justify-content-between p-2 border rounded align-items-center">
                    <span>Payment Status:</span>
                    <Badge color={data.payment_status === "Completed" ? "success" : "warning"}>{data.payment_status}</Badge>
                  </div>
                </Col>
              </Row>

              <Row className="mb-5">
                {/* CHANGED: Used xs={6} for fixed width on all screen sizes */}
                <Col xs={6}>
                  <h5 className="mb-3">From</h5>
                  <p className="mb-1"><strong>Organization ID:</strong> {data.payment_organisation_id}</p>
                  <p className="mb-1"><strong>Email Address:</strong> (Not Available)</p>
                  <p className="mb-1"><strong>Phone Number:</strong> (Not Available)</p>
                  <p className="mb-0"><strong>Address:</strong> (Not Available)</p>
                </Col>
                {/* CHANGED: Used xs={6} for fixed width on all screen sizes */}
                <Col xs={6}>
                  <h5 className="mb-3">Bill To</h5>
                  <p className="mb-1"><strong>Client ID:</strong> {data.payment_from_id}</p>
                  <p className="mb-1"><strong>Role:</strong> {data.payment_from_role}</p>
                  <p className="mb-1"><strong>Email Address:</strong> (Not Available)</p>
                  <p className="mb-0"><strong>Address:</strong> (Not Available)</p>
                </Col>
              </Row>

              <Table responsive bordered>
                <thead className="table-primary">
                  <tr>
                    <th>Item</th>
                    <th>Type</th>
                    <th>Payment Mode</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{data.payment_description}</td>
                    <td>{data.payment_type}</td>
                    <td>{data.payment_mode}</td>
                    <td className="text-end">${data.payment_amount.toFixed(2)} {data.payment_currency}</td>
                  </tr>
                </tbody>
              </Table>
              
              <Row className="mt-4">
                {/* CHANGED: Used xs={7} for fixed width on all screen sizes */}
                <Col xs={7}>
                  <h5 className="mb-3">Transaction Details</h5>
                  <div className="border rounded p-3">
                     <p className="mb-2 d-flex justify-content-between">
                         <span>Gateway Transaction ID:</span>
                         <strong>{data.payment_gateway_transaction_number}</strong>
                     </p>
                     <p className="mb-0 d-flex justify-content-between">
                         <span>Card Number Used:</span>
                         <strong>{data.payment_card_number}</strong>
                     </p>
                  </div>
                </Col>
                {/* CHANGED: Used xs={5} for fixed width on all screen sizes */}
                <Col xs={5}>
                  <div className="text-end">
                    <div className="d-flex justify-content-between mb-2">
                      <span>SubTotal:</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Transaction Charge (Tax):</span>
                      <span>${tax.toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-3 fw-bold">
                      <span>Total Paid Amount:</span>
                      <span>${paidAmount.toFixed(2)}</span>
                    </div>
                    <div className="bg-primary text-white p-3 rounded d-flex justify-content-between">
                      <h5 className="mb-0">Due Balance</h5>
                      <h5 className="mb-0">${dueBalance.toFixed(2)}</h5>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </div>
          {/* Footer is outside the ref and has d-print-none to hide it */}
          <CardFooter className="bg-primary text-white p-3 d-flex justify-content-end align-items-center d-print-none">
            <div>
              <Button color="secondary" onClick={handlePrint}>Print Invoice</Button>
            </div>
          </CardFooter>
        </Card>
      </Container>
    </>
  );
}

export default ViewRecieptSection;